import React, { useState, useEffect, useCallback } from 'react';
import {
  FaAward,
  FaHistory,
  FaPlusCircle,
  FaMinusCircle,
  FaCog,
  FaShieldAlt,
  FaSearch,
  FaCheck,
  FaTimes,
  FaSave,
  FaUsers,
  FaUserTie,
  FaEdit,
  FaSeedling,
  FaExclamationTriangle,
  FaGift,
  FaClock,
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './LoyaltyManagement.css';

// Canonical definition of all point rules, grouped by user type
const CUSTOMER_RULES = [
  { key: 'customer_signup_points', label: 'Sign Up Bonus', icon: '🎉', unit: 'pts', default: 50, description: 'Points awarded when a customer creates an account' },
  { key: 'customer_profile_completion_points', label: 'Profile Completion', icon: '👤', unit: 'pts', default: 100, description: 'Points for completing profile details' },
  { key: 'customer_first_booking_points', label: 'First Booking Bonus', icon: '📅', unit: 'pts', default: 200, description: 'One-time bonus for making their first booking' },
  { key: 'customer_signup_wallet_credit', label: 'Signup Wallet Credit ($)', icon: '💰', unit: '$', default: 5, description: 'Dollar amount credited to wallet automatically on customer signup' },
  { key: 'customer_spend_points_per_dollar', label: 'Spend Points (per $1)', icon: '💳', unit: 'pts/$1', default: 1, description: 'Points earned for every $1 spent on a booking' },
  { key: 'customer_review_points', label: 'Leave a Review', icon: '⭐', unit: 'pts', default: 50, description: 'Points for leaving a review after a booking' },
  { key: 'customer_referral_points', label: 'Successful Referral', icon: '🤝', unit: 'pts', default: 300, description: 'Points when referred user completes their first booking' },
];

const PROVIDER_RULES = [
  { key: 'provider_signup_points', label: 'Sign Up Bonus', icon: '🎉', unit: 'pts', default: 50, description: 'Points awarded when a caregiver creates an account' },
  { key: 'provider_profile_completion_points', label: 'Profile Completion', icon: '👤', unit: 'pts', default: 100, description: 'Points for completing caregiver profile' },
  { key: 'provider_verification_points', label: 'Verification Approved', icon: '🛡️', unit: 'pts', default: 200, description: 'Points when provider is verified by admin' },
  { key: 'provider_booking_points', label: 'Completed Booking', icon: '✅', unit: 'pts', default: 100, description: 'Points per completed booking' },
  { key: 'provider_review_5star_points', label: '5-Star Review Received', icon: '🌟', unit: 'pts', default: 50, description: 'Points for receiving a 5-star review' },
  { key: 'provider_referral_points', label: 'Successful Referral', icon: '🤝', unit: 'pts', default: 300, description: 'Points when a referred provider completes their first booking' },
];

const EXPIRY_RULES = [
  { key: 'points_expiry_months', label: 'Points Expiry', icon: '⏰', unit: 'months', default: 6, description: 'Number of months before earned points expire' },
];

const LoyaltyManagement = () => {
  const [activeTab, setActiveTab] = useState('rules');
  const [loading, setLoading] = useState(true);
  const [settingsMap, setSettingsMap] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustment, setAdjustment] = useState({ points: 50, description: '' });
  const [txLoading, setTxLoading] = useState(false);
  const [newRule, setNewRule] = useState({ key: '', value: '', description: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Rewards State
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [editingRewardId, setEditingRewardId] = useState(null);
  const [rewardForm, setRewardForm] = useState({ title: '', pointsRequired: 500, rewardType: 'DISCOUNT', rewardValue: '5', description: '', targetUserType: 'parent' });
  const [showRewardForm, setShowRewardForm] = useState(false);

  // Load settings from DB. No seed call — defaults are shown from rule definitions.
  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get(ENDPOINTS.LOYALTY.ADMIN_SETTINGS);
      // Unwrap TransformInterceptor: { success, data, timestamp }
      const list = Array.isArray(res) ? res : (res?.data || []);
      const map = {};
      list.forEach(s => { map[s.key] = s; });
      setSettingsMap(map);
    } catch (error) {
      // Silently ignore — we'll show hardcoded defaults
      console.warn('Could not load settings from server:', error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const res = await api.get(ENDPOINTS.LOYALTY.ADMIN_TRANSACTIONS);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setTransactions(list);
    } catch (error) {
      console.error(error);
    } finally {
      setTxLoading(false);
    }
  }, []);

  const fetchRewards = useCallback(async () => {
    setRewardsLoading(true);
    try {
      const res = await api.get(ENDPOINTS.LOYALTY.ADMIN_REWARDS);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setRewards(list);
    } catch (e) {
      console.warn('Could not load rewards', e?.message);
    } finally {
      setRewardsLoading(false);
    }
  }, []);

  const fetchRedemptions = useCallback(async () => {
    setRedemptionsLoading(true);
    try {
      const res = await api.get(ENDPOINTS.LOYALTY.ADMIN_REDEMPTIONS);
      const list = Array.isArray(res) ? res : (res?.data || []);
      setRedemptions(list);
    } catch (e) {
      console.warn('Could not load redemptions', e?.message);
    } finally {
      setRedemptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    if (activeTab === 'history') fetchTransactions();
    if (activeTab === 'rewards') fetchRewards();
    if (activeTab === 'redemptions') fetchRedemptions();
  }, [activeTab, fetchSettings, fetchTransactions, fetchRewards, fetchRedemptions]);

  // Get value: from DB if saved, otherwise use rule's hardcoded default
  const getVal = (key, fallback = 0) => {
    return settingsMap[key] !== undefined ? Number(settingsMap[key].value) : fallback;
  };

  const startEdit = (key, currentValue) => {
    setEditingKey(key);
    setEditingValue(String(currentValue));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingValue('');
  };

  const saveEdit = async (key) => {
    const val = Number(editingValue);
    if (isNaN(val) || val < 0) {
      toast.warning('Please enter a valid positive number');
      return;
    }
    try {
      // Send ONLY key + value — no extra fields — works with both old and new server validation
      await api.post(ENDPOINTS.LOYALTY.ADMIN_SETTINGS, { key, value: val });
      toast.success('Updated successfully!');
      setEditingKey(null);
      fetchSettings();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to save: ${msg}`);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.key || newRule.value === '') {
      toast.warning('Key and value are required');
      return;
    }
    try {
      await api.post(ENDPOINTS.LOYALTY.ADMIN_SETTINGS, {
        key: newRule.key.toLowerCase().replace(/\s+/g, '_'),
        value: Number(newRule.value),
      });
      toast.success('Custom rule created!');
      setNewRule({ key: '', value: '', description: '' });
      setShowCreateForm(false);
      fetchSettings();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unknown error';
      toast.error(`Failed to create rule: ${msg}`);
    }
  };

  const handleSaveReward = async () => {
    if (!rewardForm.title || rewardForm.pointsRequired === '' || rewardForm.rewardValue === '') {
      toast.warning('Please fill all required fields');
      return;
    }
    try {
      const payload = {
        ...rewardForm,
        pointsRequired: Number(rewardForm.pointsRequired)
      };
      if (editingRewardId) {
        await api.patch(`${ENDPOINTS.LOYALTY.ADMIN_REWARDS}/${editingRewardId}`, payload);
        toast.success('Reward updated');
      } else {
        await api.post(ENDPOINTS.LOYALTY.ADMIN_REWARDS, payload);
        toast.success('Reward created');
      }
      setEditingRewardId(null);
      setShowRewardForm(false);
      setRewardForm({ title: '', pointsRequired: 500, rewardType: 'DISCOUNT', rewardValue: '5', description: '' });
      fetchRewards();
    } catch (e) {
      toast.error('Failed to save reward');
    }
  };

  const handleDeleteReward = async (id) => {
    if (!window.confirm('Delete this reward?')) return;
    try {
      await api.delete(`${ENDPOINTS.LOYALTY.ADMIN_REWARDS}/${id}`);
      toast.success('Reward deleted');
      fetchRewards();
    } catch (e) {
      toast.error('Failed to delete reward');
    }
  };

  const handleSearchUser = async () => {
    if (!searchQuery) return;
    try {
      const [custRes, provRes] = await Promise.all([
        api.get(ENDPOINTS.CUSTOMERS.base, { params: { search: searchQuery } }),
        api.get(ENDPOINTS.PROVIDERS.base, { params: { search: searchQuery } }),
      ]);
      // Unwrap the TransformInterceptor envelope: { success, data, timestamp }
      const customers = Array.isArray(custRes) ? custRes : (custRes?.data || custRes?.data?.data || []);
      const providers = Array.isArray(provRes) ? provRes : (provRes?.data || provRes?.data?.data || []);
      const results = [
        ...customers.map(u => ({ ...u, userType: 'parent' })),
        ...providers.map(u => ({ ...u, userType: 'caregiver' })),
      ];
      setUsers(results);
      if (results.length === 0) toast.info('No users found');
    } catch (error) {
      toast.error('User search failed');
    }
  };

  const handleAdjustPoints = async () => {
    if (!selectedUser || !adjustment.description) {
      toast.warning('Please select a user and provide a description');
      return;
    }
    try {
      await api.post(ENDPOINTS.LOYALTY.ADMIN_GIVE_POINTS, {
        userId: selectedUser.id,
        userType: selectedUser.userType,
        points: adjustment.points,
        description: adjustment.description,
      });
      toast.success(`Points ${adjustment.points >= 0 ? 'added' : 'removed'} successfully!`);
      setAdjustment({ points: 50, description: '' });
      setSelectedUser(null);
      setUsers([]);
      setSearchQuery('');
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to adjust points');
    }
  };

  const renderRuleRow = (rule) => {
    // Show DB value if saved, else show rule's built-in default
    const savedEntry = settingsMap[rule.key];
    const displayValue = savedEntry !== undefined ? Number(savedEntry.value) : rule.default;
    const isEditing = editingKey === rule.key;
    const isSaved = savedEntry !== undefined;

    return (
      <div key={rule.key} className={`rule-row ${!isSaved ? 'rule-not-set' : ''}`}>
        <div className="rule-icon">{rule.icon}</div>
        <div className="rule-details">
          <div className="rule-label">{rule.label}</div>
          <div className="rule-desc">{rule.description}</div>
        </div>
        <div className="rule-value-area">
          {isEditing ? (
            <div className="rule-edit-inline">
              <input
                type="number"
                className="rule-edit-input"
                value={editingValue}
                min={0}
                autoFocus
                onChange={e => setEditingValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveEdit(rule.key);
                  if (e.key === 'Escape') cancelEdit();
                }}
              />
              <span className="rule-unit">{rule.unit}</span>
              <button className="btn-icon btn-save" title="Save" onClick={() => saveEdit(rule.key)}>
                <FaCheck />
              </button>
              <button className="btn-icon btn-cancel" title="Cancel" onClick={cancelEdit}>
                <FaTimes />
              </button>
            </div>
          ) : (
            <div className="rule-display" onClick={() => startEdit(rule.key, displayValue)}>
              <span className={`rule-value-badge ${!isSaved ? 'default-val' : ''}`}>
                {displayValue}
              </span>
              <span className="rule-unit">{rule.unit}</span>
              {!isSaved && <span className="default-tag">default</span>}
              <button className="btn-icon btn-edit" title="Edit">
                <FaEdit />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <div className="modern-spinner"></div>
        <p className="loading-text">Loading Loyalty Settings...</p>
      </div>
    );
  }

  return (
    <div className="loyalty-mgmt-page">
      <div className="page-header">
        <div>
          <h1><FaAward /> Loyalty Program Management</h1>
          <p>Configure rewarding rules, adjust user points, and monitor program activity.</p>
        </div>
        {/* <button className="btn-seed" onClick={() => fetchSettings()} title="Refresh settings from server">
          <FaSeedling /> Refresh Settings
        </button> */}
      </div>

      <div className="loyalty-tabs">
        <button className={activeTab === 'rules' ? 'active' : ''} onClick={() => setActiveTab('rules')}>
          <FaCog /> Point Rules
        </button>
        {/* <button className={activeTab === 'adjust' ? 'active' : ''} onClick={() => setActiveTab('adjust')}>
          <FaPlusCircle /> Manual Adjustments
        </button> */}
        <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
          <FaHistory /> Transaction History
        </button>
        <button className={activeTab === 'rewards' ? 'active' : ''} onClick={() => setActiveTab('rewards')}>
          <FaGift /> Rewards Management
        </button>
        <button className={activeTab === 'redemptions' ? 'active' : ''} onClick={() => setActiveTab('redemptions')}>
          <FaHistory /> Redemption History
        </button>
      </div>

      <div className="loyalty-content">
        {/* ==================== POINT RULES TAB ==================== */}
        {activeTab === 'rules' && (
          <div className="rules-container">
            {/* Customer Section */}
            <div className="rules-section">
              <div className="section-header">
                <FaUsers className="section-icon customer-icon" />
                <div>
                  <h2>Customer (Parent) Point Rules</h2>
                  <p>Points earned by families using the platform</p>
                </div>
              </div>
              <div className="rules-list">
                {CUSTOMER_RULES.map(renderRuleRow)}
              </div>
            </div>

            {/* Provider Section */}
            {/* <div className="rules-section provider-section">
              <div className="section-header">
                <FaUserTie className="section-icon provider-icon" />
                <div>
                  <h2>Provider (Caregiver) Point Rules</h2>
                  <p>Points earned by caregivers for their service</p>
                </div>
              </div>
              <div className="rules-list">
                {PROVIDER_RULES.map(renderRuleRow)}
              </div>
            </div> */}

            {/* Expiry Section */}
            <div className="rules-section expiry-section">
              <div className="section-header">
                <FaClock className="section-icon expiry-icon" />
                <div>
                  <h2>Expiry Policy</h2>
                  <p>Set how long earned points remain valid</p>
                </div>
              </div>
              <div className="rules-list">
                {EXPIRY_RULES.map(renderRuleRow)}
              </div>
            </div>

            {/* Redemption Tiers CRUD Display */}
            {/* <div className="rules-section redemption-section">
              <div className="section-header">
                <FaGift className="section-icon redemption-icon" />
                <div>
                  <h2>Redemption Tiers (Rewards)</h2>
                  <p>Configure what users can redeem with their points</p>
                </div>
                <button 
                  className="btn-toggle-create" 
                  onClick={() => {
                     setEditingRewardId(null);
                     setRewardForm({ title: '', pointsRequired: 500, rewardType: 'DISCOUNT', rewardValue: '5', description: '', targetUserType: 'parent' });
                     setShowRewardForm(v => !v);
                  }}
                >
                  {showRewardForm ? 'Cancel' : '+ New Reward'}
                </button>
              </div>

              {showRewardForm && (
                <div className="create-form">
                  <div className="create-form-grid">
                    <div className="form-group">
                      <label>Title <span className="hint">(e.g. $10 Off Booking)</span></label>
                      <input
                        type="text"
                        placeholder="Title"
                        value={rewardForm.title}
                        onChange={e => setRewardForm({ ...rewardForm, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Points Required</label>
                      <input
                        type="number"
                        placeholder="500"
                        min={0}
                        value={rewardForm.pointsRequired}
                        onChange={e => setRewardForm({ ...rewardForm, pointsRequired: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reward Type</label>
                      <select 
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                        value={rewardForm.rewardType}
                        onChange={e => setRewardForm({ ...rewardForm, rewardType: e.target.value })}
                      >
                        <option value="DISCOUNT">Discount ($ Off)</option>
                        <option value="FREE_SERVICE">Free Service</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Value <span className="hint">($ amount or code)</span></label>
                      <input
                        type="text"
                        placeholder="e.g. 10"
                        value={rewardForm.rewardValue}
                        onChange={e => setRewardForm({ ...rewardForm, rewardValue: e.target.value })}
                      />
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={handleSaveReward}>
                    <FaSave /> Save Reward
                  </button>
                </div>
              )}

              <div className="table-wrapper" style={{ marginTop: '1rem' }}>
                <table className="loyalty-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Points</th>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rewards.filter(r => r.isActive).map((reward) => (
                      <tr key={reward.id}>
                        <td><strong>{reward.title}</strong></td>
                        <td>{reward.pointsRequired} pts</td>
                        <td><span className="tx-type">{reward.rewardType?.replace('_', ' ')}</span></td>
                        <td>{reward.rewardType === 'DISCOUNT' ? `$${reward.rewardValue}` : reward.rewardValue}</td>
                        <td><span className={`status ${reward.isActive ? 'active' : 'inactive'}`}>{reward.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <button className="btn-icon btn-edit" title="Edit" onClick={() => {
                             setEditingRewardId(reward.id);
                             setRewardForm({ 
                               title: reward.title, 
                               pointsRequired: reward.pointsRequired, 
                               rewardType: reward.rewardType || 'DISCOUNT', 
                               rewardValue: reward.rewardValue || String(reward.discountAmount), 
                               description: reward.description || '',
                               targetUserType: reward.targetUserType || 'parent'
                             });
                             setShowRewardForm(true);
                          }}>
                            <FaEdit />
                          </button>
                          <button className="btn-icon btn-cancel" title="Delete" onClick={() => handleDeleteReward(reward.id)}>
                            <FaTimes />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rewards.filter(r => r.isActive).length === 0 && !rewardsLoading && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '1rem' }}>No active rewards found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div> */}

            {/* Create Custom Rule */}
            {/* <div className="rules-section create-section">
              <div className="section-header">
                <FaPlusCircle className="section-icon create-icon" />
                <div>
                  <h2>Create Custom Rule</h2>
                  <p>Add a new point setting key manually</p>
                </div>
                <button className="btn-toggle-create" onClick={() => setShowCreateForm(v => !v)}>
                  {showCreateForm ? 'Cancel' : '+ New Rule'}
                </button>
              </div>
              {showCreateForm && (
                <div className="create-form">
                  <div className="create-form-grid">
                    <div className="form-group">
                      <label>Setting Key <span className="hint">(e.g. customer_signup_points)</span></label>
                      <input
                        type="text"
                        placeholder="setting_key_name"
                        value={newRule.key}
                        onChange={e => setNewRule({ ...newRule, key: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Value (Points)</label>
                      <input
                        type="number"
                        placeholder="50"
                        min={0}
                        value={newRule.value}
                        onChange={e => setNewRule({ ...newRule, value: e.target.value })}
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Description</label>
                      <input
                        type="text"
                        placeholder="Describe what this rule does..."
                        value={newRule.description}
                        onChange={e => setNewRule({ ...newRule, description: e.target.value })}
                      />
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={handleCreateRule}>
                    <FaSave /> Create Rule
                  </button>
                </div>
              )}
            </div> */}
          </div>
        )}

        {/* ==================== MANUAL ADJUSTMENTS TAB ==================== */}
        {activeTab === 'adjust' && (
          <div className="adjust-container">
            <div className="user-search-section content-card">
              <h3><FaSearch /> Find User</h3>
              <p className="section-subtitle">Search by name or email to find a customer or provider</p>
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Type name or email..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearchUser()}
                />
                <button className="btn-search" onClick={handleSearchUser}><FaSearch /></button>
              </div>

              {users.length > 0 && (
                <div className="user-results">
                  {users.map(user => (
                    <div
                      key={`${user.userType}-${user.id}`}
                      className={`user-result-item ${selectedUser?.id === user.id && selectedUser?.userType === user.userType ? 'selected' : ''}`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className="user-avatar-placeholder">{(user.firstName || user.name || 'U')[0].toUpperCase()}</div>
                      <div className="user-info">
                        <strong>{user.firstName} {user.lastName}</strong>
                        <span>{user.email}</span>
                      </div>
                      <span className={`user-type-badge ${user.userType}`}>
                        {user.userType === 'parent' ? 'Customer' : 'Provider'}
                      </span>
                      <div className="user-points-display">
                        {user.loyaltyPoints || 0} pts
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedUser && (
              <div className="adjustment-form content-card">
                <div className="selected-user-banner">
                  <div className="selected-avatar">{(selectedUser.firstName || 'U')[0].toUpperCase()}</div>
                  <div>
                    <h3>{selectedUser.firstName} {selectedUser.lastName}</h3>
                    <span>{selectedUser.email} &bull; <span className={`user-type-badge ${selectedUser.userType}`}>{selectedUser.userType === 'parent' ? 'Customer' : 'Provider'}</span></span>
                  </div>
                  <button className="btn-icon btn-cancel" onClick={() => setSelectedUser(null)}><FaTimes /></button>
                </div>

                <div className="quick-actions">
                  {[50, 100, 200, 300, -50, -100].map(pt => (
                    <button
                      key={pt}
                      className={`quick-btn ${pt > 0 ? 'add' : 'remove'}`}
                      onClick={() => setAdjustment(a => ({ ...a, points: pt }))}
                    >
                      {pt > 0 ? `+${pt}` : pt} pts
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label>Point Amount <span className="hint">(negative to remove points)</span></label>
                  <input
                    type="number"
                    value={adjustment.points}
                    onChange={e => setAdjustment({ ...adjustment, points: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Reason / Description *</label>
                  <textarea
                    placeholder="e.g., Manual bonus for event participation"
                    value={adjustment.description}
                    onChange={e => setAdjustment({ ...adjustment, description: e.target.value })}
                  />
                </div>
                <div className="adjust-actions">
                  <button className={`btn ${adjustment.points >= 0 ? 'btn-primary' : 'btn-danger'}`} onClick={handleAdjustPoints}>
                    {adjustment.points >= 0 ? <FaPlusCircle /> : <FaMinusCircle />}
                    {' '}{adjustment.points >= 0 ? `Add ${adjustment.points} Points` : `Remove ${Math.abs(adjustment.points)} Points`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== REDEMPTION HISTORY TAB ==================== */}
        {activeTab === 'redemptions' && (
          <div className="history-container">
            <div className="table-wrapper content-card">
              <div className="section-header">
                <h3><FaHistory /> Reward Redemption History</h3>
                <button className="btn-refresh" onClick={fetchRedemptions}><FaSeedling /> Refresh</button>
              </div>
              <table className="loyalty-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User ID</th>
                    <th>User Name</th>
                    <th>Role</th>
                    <th>Reward</th>
                    <th>Points Used</th>
                    <th>Discount</th>
                    <th>Booking</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((red, i) => (
                    <tr key={red.id}>
                      <td className="tx-num">{i + 1}</td>
                      <td>#{red.userId}</td>
                      <td style={{ fontWeight: 600 }}>{red.userFullName || 'Unknown User'}</td>
                      <td><span className={`badge ${red.userType}`}>{red.userType === 'parent' ? 'Customer' : 'Provider'}</span></td>
                      <td><strong>{red.reward?.title}</strong></td>
                      <td className="pts-minus">-{red.pointsUsed}</td>
                      <td>{Number(red.discountApplied) > 0 ? `$${Number(red.discountApplied).toFixed(2)}` : (red.reward?.rewardValue || 'FREE')}</td>
                      <td>{red.bookingId ? `#${red.bookingId}` : '-'}</td>
                      <td><span className={`status ${red.status}`}>{red.status}</span></td>
                      <td>{new Date(red.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {redemptions.length === 0 && !redemptionsLoading && (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No redemption records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {redemptionsLoading && (
                <div className="table-loader">
                  <div className="modern-spinner small"></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== TRANSACTION HISTORY TAB ==================== */}
        {activeTab === 'history' && (
          <div className="history-section content-card">
            <div className="history-header">
              <h3><FaHistory /> All Transactions</h3>
              <button className="btn-refresh" onClick={fetchTransactions}>{txLoading ? 'Loading...' : '↻ Refresh'}</button>
            </div>
            {txLoading ? (
              <div className="table-loading">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="empty-state">
                <FaHistory />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="loyalty-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User ID</th>
                      <th>User Name</th>
                      <th>Role</th>
                      <th>Type</th>
                      <th>Reward / Change</th>
                      <th>Status</th>
                      <th>Description</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={tx.id}>
                        <td className="tx-num">{i + 1}</td>
                        <td>#{tx.userId}</td>
                        <td style={{ fontWeight: 600 }}>{tx.userFullName || 'Unknown User'}</td>
                        <td><span className={`badge ${tx.userType}`}>{tx.userType === 'parent' ? 'Customer' : 'Provider'}</span></td>
                        <td><span className="tx-type">{tx.type?.replace(/_/g, ' ')}</span></td>
                        <td className={tx.points !== 0 ? (tx.points > 0 ? 'pts-plus' : 'pts-minus') : (Number(tx.amount) !== 0 ? 'amt-plus' : '')}>
                          {tx.points !== 0 ? (tx.points > 0 ? `+${tx.points}` : tx.points) :
                            (Number(tx.amount) !== 0 ? `+$${Number(tx.amount).toFixed(2)}` : '0')}
                        </td>
                        <td><span className={`status ${tx.status}`}>{tx.status}</span></td>
                        <td className="tx-desc">{tx.description}</td>
                        <td>{new Date(tx.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ==================== REWARDS MANAGEMENT TAB ==================== */}
        {activeTab === 'rewards' && (
          <div className="rewards-mgmt-container">
            <div className="rules-section redemption-section">
              <div className="section-header">
                <FaGift className="section-icon redemption-icon" />
                <div>
                  <h2>Redemption Tiers (Rewards)</h2>
                  <p>Configure what users can redeem with their points</p>
                </div>
                <button
                  className="btn-toggle-create"
                  onClick={() => {
                    setEditingRewardId(null);
                    setRewardForm({ title: '', pointsRequired: 500, rewardType: 'DISCOUNT', rewardValue: '5', description: '', targetUserType: 'parent' });
                    setShowRewardForm(v => !v);
                  }}
                >
                  {showRewardForm ? 'Cancel' : '+ New Reward'}
                </button>
              </div>

              {showRewardForm && (
                <div className="create-form">
                  <div className="create-form-grid">
                    <div className="form-group">
                      <label>Title <span className="hint">(e.g. $10 Off Booking)</span></label>
                      <input
                        type="text"
                        placeholder="Title"
                        value={rewardForm.title}
                        onChange={e => setRewardForm({ ...rewardForm, title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Points Required</label>
                      <input
                        type="number"
                        placeholder="500"
                        min={0}
                        value={rewardForm.pointsRequired}
                        onChange={e => setRewardForm({ ...rewardForm, pointsRequired: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reward Type</label>
                      <select
                        className="loyalty-select"
                        value={rewardForm.rewardType}
                        onChange={e => setRewardForm({ ...rewardForm, rewardType: e.target.value })}
                      >
                        <option value="DISCOUNT">Discount ($ Off)</option>
                        <option value="FREE_SERVICE">Free Service / Hour</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Target Audience</label>
                      <select
                        className="loyalty-select"
                        value={rewardForm.targetUserType}
                        onChange={e => setRewardForm({ ...rewardForm, targetUserType: e.target.value })}
                      >
                        <option value="parent">Customers (Parents) Only</option>
                        <option value="caregiver">Providers (Caregivers) Only</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Value <span className="hint">($ amount or description)</span></label>
                      <input
                        type="text"
                        placeholder="e.g. 10 or 1 FREE HOUR"
                        value={rewardForm.rewardValue}
                        onChange={e => setRewardForm({ ...rewardForm, rewardValue: e.target.value })}
                      />
                    </div>
                    <div className="form-group full-width">
                      <label>Description (Optional)</label>
                      <textarea
                        placeholder="Small print or details..."
                        value={rewardForm.description}
                        onChange={e => setRewardForm({ ...rewardForm, description: e.target.value })}
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1.5px solid #dfe6e9', minHeight: '80px' }}
                      />
                    </div>
                  </div>
                  <button className="btn btn-primary" onClick={handleSaveReward}>
                    <FaSave /> {editingRewardId ? 'Update Reward' : 'Save Reward'}
                  </button>
                </div>
              )}

              <div className="table-wrapper" style={{ marginTop: '1rem' }}>
                <table className="loyalty-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Points</th>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Audience</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rewards.filter(r => r.isActive).map((reward) => (
                      <tr key={reward.id}>
                        <td><strong>{reward.title}</strong></td>
                        <td>{reward.pointsRequired} pts</td>
                        <td><span className={`tx-type ${reward.rewardType?.toLowerCase()}`}>{reward.rewardType?.replace('_', ' ')}</span></td>
                        <td>{reward.rewardType === 'DISCOUNT' ? `$${reward.rewardValue ?? reward.discountAmount}` : (reward.rewardValue ?? reward.discountAmount)}</td>
                        <td><span className={`user-type-badge ${reward.targetUserType}`}>{reward.targetUserType === 'both' ? 'Everyone' : reward.targetUserType === 'parent' ? 'Customers' : 'Providers'}</span></td>
                        <td><span className={`status ${reward.isActive ? 'active' : 'inactive'}`}>{reward.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td className="actions-cell">
                          <button className="btn-icon btn-edit" title="Edit" onClick={() => {
                            setEditingRewardId(reward.id);
                            setRewardForm({
                              title: reward.title,
                              pointsRequired: reward.pointsRequired,
                              rewardType: reward.rewardType || 'DISCOUNT',
                              rewardValue: reward.rewardValue || String(reward.discountAmount),
                              description: reward.description || '',
                              targetUserType: reward.targetUserType || 'parent'
                            });
                            setShowRewardForm(true);
                          }}>
                            <FaEdit />
                          </button>
                          <button className="btn-icon btn-cancel" title="Delete" onClick={() => handleDeleteReward(reward.id)}>
                            <FaTimes />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {rewards.filter(r => r.isActive).length === 0 && !rewardsLoading && (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                          <div className="empty-state">
                            <FaGift size={32} color="#b2bec3" />
                            <p>No active rewards found.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== ABUSE PREVENTION TAB ==================== */}
        {activeTab === 'fraud' && (
          <div className="fraud-section">
            <div className="alert-banner">
              <FaExclamationTriangle />
              <span>System is actively monitoring for suspicious activity patterns.</span>
            </div>
            <div className="fraud-grid">
              <div className="content-card">
                <h3><FaShieldAlt /> Protection Checklist</h3>
                <ul className="fraud-list">
                  <li className="ok"><FaCheck /> Duplicate signup bonus prevention (one-time only)</li>
                  <li className="ok"><FaCheck /> Profile completion bonus is idempotent</li>
                  <li className="ok"><FaCheck /> First booking bonus awarded only once per user</li>
                  <li className="ok"><FaCheck /> Points reversed on booking cancellation</li>
                  <li className="warn"><FaExclamationTriangle /> Fake review loop monitoring</li>
                  <li className="warn"><FaExclamationTriangle /> Self-referral detection</li>
                </ul>
              </div>
              <div className="content-card">
                <h3>System Rules</h3>
                <div className="fraud-rules-grid">
                  <div className="fraud-rule-item">
                    <div className="fraud-rule-icon">🔒</div>
                    <div>
                      <strong>Idempotent Awards</strong>
                      <p>Signup & profile bonuses are only awarded once per account, even if triggered multiple times.</p>
                    </div>
                  </div>
                  <div className="fraud-rule-item">
                    <div className="fraud-rule-icon">⏱️</div>
                    <div>
                      <strong>Expiry System</strong>
                      <p>Points expire after {getVal('points_expiry_months', 6)} months to prevent accumulation hoarding.</p>
                    </div>
                  </div>
                  <div className="fraud-rule-item">
                    <div className="fraud-rule-icon">🔄</div>
                    <div>
                      <strong>Auto Reversal</strong>
                      <p>Redemption points are fully refunded if a booking is cancelled or reversed.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyManagement;
